import type {RichTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {useLinkResolver} from 'prismicio'
import type {ButtonDocument} from 'prismicio-types'
import {ButtonV2} from 'src/common/ButtonV2'
import {DeferredVideo} from 'src/common/DeferredVideo'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'
import {AnimatedBlobBackground} from './AnimatedBlobBackground'

export type BlobSectionButtonData = ButtonDocument['data'] | null

const descriptionWidthMap: Record<string, string> = {
  'Small (432px)': 'max-w-[432px]',
  'Medium (560px)': 'max-w-[560px]',
  'Large (680px)': 'max-w-[680px]',
}

export type BlobSectionUIProps = {
  eyebrow: string
  title: RichTextField
  description: string
  descriptionWidth?: string | null
  primaryButtonData: BlobSectionButtonData
  secondaryButtonData: BlobSectionButtonData
  disclaimer: string
  videoUrl: string | null
  videoPosterUrl?: string | null
  pageUid: string
  paddingTop?: string | null
  paddingBottom?: string | null
}

export const BlobSection = ({
  eyebrow,
  title,
  description,
  descriptionWidth,
  primaryButtonData,
  secondaryButtonData,
  disclaimer,
  videoUrl,
  videoPosterUrl,
  pageUid,
  paddingTop,
  paddingBottom,
}: BlobSectionUIProps) => {
  const {linkResolver} = useLinkResolver()
  const paddingTopClass = PADDING_TOP_CLASS[paddingTop ?? DEFAULT_PADDING] ?? ''
  const paddingBottomClass = PADDING_BOTTOM_CLASS[paddingBottom ?? DEFAULT_PADDING] ?? ''

  return (
    <section
      data-slice-type="blob_section"
      data-slice-variation="default"
      data-nav-dark="true"
      className={cn(
        'relative flex min-h-[500px] items-center overflow-hidden bg-green-dark px-5 md:min-h-[650px] md:px-10 lg:min-h-[825px] lg:px-[120px] ',
        paddingTopClass,
        paddingBottomClass,
        'max-md:!pb-24',
        '[&:first-child]:pt-40',
      )}
    >
      <AnimatedBlobBackground />
      <div className="relative z-10 flex w-full flex-col items-center gap-12 lg:gap-[120px]">
        <div className="flex flex-col items-center gap-8">
          <div className="flex flex-col items-center gap-4 text-center">
            {eyebrow && (
              <p className="font-main text-12 font-semibold uppercase tracking-[0.6px] text-moss-white">{eyebrow}</p>
            )}
            <h1 className="flex flex-col">
              <PrismicRichText
                field={title}
                components={{
                  heading1: ({children}) => (
                    <span className="font-main text-40 leading-[42px] tracking-[-0.4px] text-moss-white md:leading-tight lg:text-64 lg:leading-[64px] lg:tracking-[-0.64px]">
                      {children}
                    </span>
                  ),
                  em: () => <br />,
                }}
              />
            </h1>
            {description && (
              <p
                className={cn(
                  'mt-2 font-main text-18 leading-6 text-moss-white md:mt-4',
                  descriptionWidthMap[descriptionWidth ?? 'Small (432px)'] ?? 'max-w-[432px]',
                )}
              >
                {description}
              </p>
            )}
          </div>
          {(primaryButtonData?.label || secondaryButtonData?.label) && (
            <div className="flex flex-col items-center gap-3">
              <div className="flex flex-wrap items-center justify-center gap-4">
                {primaryButtonData?.label && isFilled.link(primaryButtonData.link) && (
                  <ButtonV2
                    variant="primary"
                    href={linkResolver(primaryButtonData.link, 'blob-section')}
                    trackingData={{
                      location: 'blob-section',
                      dataType: 'primary-button',
                      pageName: pageUid,
                      mktgId: primaryButtonData.marketing_id ?? '',
                      label: primaryButtonData.label,
                    }}
                  >
                    {primaryButtonData.label}
                  </ButtonV2>
                )}
                {secondaryButtonData?.label && isFilled.link(secondaryButtonData.link) && (
                  <ButtonV2
                    variant="secondary"
                    href={linkResolver(secondaryButtonData.link, 'blob-section')}
                    trackingData={{
                      location: 'blob-section',
                      dataType: 'secondary-button',
                      pageName: pageUid,
                      mktgId: secondaryButtonData.marketing_id ?? '',
                      label: secondaryButtonData.label,
                    }}
                  >
                    {secondaryButtonData.label}
                  </ButtonV2>
                )}
              </div>
              {disclaimer && (
                <p className="text-center font-main text-12 font-semibold uppercase tracking-[0.6px] text-moss-white/70">
                  {disclaimer}
                </p>
              )}
            </div>
          )}
        </div>
        {videoUrl && (
          <div className="aspect-video w-full max-w-[1200px] overflow-hidden rounded-[6.4px]">
            <DeferredVideo videoUrl={videoUrl} posterUrl={videoPosterUrl} className="size-full object-cover" />
          </div>
        )}
      </div>
    </section>
  )
}
